import React, { useState, useEffect } from 'react';

function CouponForm({ coupon, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: '',
    brandName: '',
    appName: 'GPay',
    discountText: '',
    couponCode: '',
    minOrderValue: '',
    category: 'Food',
    howToAvail: '',
    termsAndConditions: '',
    redirectUrl: '',
    affiliateUrl: '',
    isFeatured: false,
    expiryDate: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (coupon) {
      setFormData({
        title: coupon.title,
        brandName: coupon.brandName,
        appName: coupon.appName,
        discountText: coupon.discountText,
        couponCode: coupon.couponCode || '',
        minOrderValue: coupon.minOrderValue || '',
        category: coupon.category,
        howToAvail: coupon.howToAvail,
        termsAndConditions: coupon.termsAndConditions,
        redirectUrl: coupon.redirectUrl,
        affiliateUrl: coupon.affiliateUrl || '',
        isFeatured: coupon.isFeatured || false,
        expiryDate: coupon.expiryDate.split('T')[0]
      });
    }
  }, [coupon]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        minOrderValue: formData.minOrderValue ? parseFloat(formData.minOrderValue) : null,
        couponCode: formData.couponCode || null,
        expiryDate: new Date(formData.expiryDate).toISOString()
      };

      await onSave(submitData);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save coupon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{coupon ? 'Edit Coupon' : 'Add New Coupon'}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Brand Name *</label>
            <input
              type="text"
              name="brandName"
              value={formData.brandName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>App Name *</label>
            <select name="appName" value={formData.appName} onChange={handleChange} required>
              <option value="GPay">GPay</option>
              <option value="Paytm">Paytm</option>
              <option value="PhonePe">PhonePe</option>
              <option value="Website">Website</option>
            </select>
          </div>

          <div className="form-group">
            <label>Discount Text *</label>
            <input
              type="text"
              name="discountText"
              value={formData.discountText}
              onChange={handleChange}
              placeholder="e.g., Flat 50% OFF"
              required
            />
          </div>

          <div className="form-group">
            <label>Coupon Code</label>
            <input
              type="text"
              name="couponCode"
              value={formData.couponCode}
              onChange={handleChange}
              placeholder="Optional"
            />
          </div>

          <div className="form-group">
            <label>Min Order Value</label>
            <input
              type="number"
              name="minOrderValue"
              value={formData.minOrderValue}
              onChange={handleChange}
              placeholder="Optional"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select name="category" value={formData.category} onChange={handleChange} required>
              <option value="Food">Food</option>
              <option value="Fashion">Fashion</option>
              <option value="Electronics">Electronics</option>
              <option value="Beauty">Beauty</option>
              <option value="Travel">Travel</option>
              <option value="Grocery">Grocery</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>How to Avail *</label>
            <textarea
              name="howToAvail"
              value={formData.howToAvail}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Terms and Conditions *</label>
            <textarea
              name="termsAndConditions"
              value={formData.termsAndConditions}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Redirect URL *</label>
            <input
              type="url"
              name="redirectUrl"
              value={formData.redirectUrl}
              onChange={handleChange}
              placeholder="https://example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Affiliate URL (for earning commissions)</label>
            <input
              type="url"
              name="affiliateUrl"
              value={formData.affiliateUrl}
              onChange={handleChange}
              placeholder="https://cuelinks.com/... or https://affiliate-network.com/..."
            />
            <small style={{ color: '#6e6e73', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Add your CueLinks, Amazon Associates, or other affiliate link here to earn commissions
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleCheckboxChange}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              <span>Mark as Featured Deal ⭐</span>
            </label>
            <small style={{ color: '#6e6e73', fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '24px' }}>
              Featured deals appear with special styling and attract more attention
            </small>
          </div>

          <div className="form-group">
            <label>Expiry Date *</label>
            <input
              type="date"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Coupon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CouponForm;
